import BaseComponent from '../../components/base-component/base-component'
import { Nullable } from '../../types/common'
import { checkMobileVersion } from '../../utils/check-media'
import { debounce } from '../../utils/debounce'
import { setStyleVariable } from '../../utils/dom'
import { bodyElement } from '../common/body-element'
import FloatElement from '../float-element/float-element'

class HeaderComponent extends BaseComponent {
  lastScrollPosition: number
  headerSectionBottom: number
  resizeObserver: ResizeObserver
  dropdowns: FloatElement[]

  constructor() {
    super()

    this.lastScrollPosition = 0
    this.dropdowns = [
      ...this.querySelectorAll('[data-header-dropdown]'),
    ] as FloatElement[]

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this)
  }

  connectedCallback(): void {
    this.setSticky()

    this.updatePosition()

    window.addEventListener('scroll', this.handleScroll)

    this.dropdowns.forEach(dropdown => {
      customElements.upgrade(dropdown) // https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry/upgrade
      customElements.whenDefined('float-element').then(() => {
        dropdown.subscribe('show', this.handleDropdownShow)
        dropdown.subscribe('hide', this.handleDrodpownHide)
      })
    })
  }

  disconnectedCallback(): void {
    window.removeEventListener('scroll', this.handleScroll)

    this.dropdowns.forEach(dropdown => {
      customElements.upgrade(dropdown) // https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry/upgrade
      customElements.whenDefined('float-element').then(() => {
        dropdown.unsubscribe('show', this.handleDropdownShow)
        dropdown.unsubscribe('hide', this.handleDrodpownHide)
      })
    })

    this.resizeObserver.disconnect()
  }

  handleResize = debounce((): void => {
    this.setHidden(false)

    this.updatePosition()

    const headerSectionRect = this.sectionElement.getBoundingClientRect()

    this.headerSectionBottom = headerSectionRect.bottom
  }, 100)

  handleScroll = debounce((): void => {
    const isMobile = checkMobileVersion()

    if (isMobile) {
      this.updateVisibleAfterScroll()
    }

    this.setScrollStatus()
  }, 100)

  handleDrodpownHide = (): void => {
    this.classList.remove('header--light-bordered')
  }

  handleDropdownShow = (): void => {
    this.classList.add('header--light-bordered')
  }

  setScrollStatus(): void {
    if (!this.sectionElement) {
      return
    }

    const savedScrollPosition = Math.abs(
      parseInt(document.body.style.top || '0'),
    )
    const scrollPosition = window.scrollY || savedScrollPosition
    const isUnderHeader = scrollPosition > this.headerSectionBottom

    this.sectionElement.toggleAttribute('scrolled', isUnderHeader)
  }

  updateVisibleAfterScroll(): void {
    if (!this.sectionElement) {
      return
    }

    if (bodyElement.isFixed) {
      return
    }

    const scrollPosition = window.scrollY

    const threshold = 48
    const scrollDistance = scrollPosition - this.lastScrollPosition

    const isScrollUp = scrollDistance < 0
    const isScrollDown = scrollDistance > 0
    const isUnderHeader = scrollPosition > this.headerSectionBottom

    if (isScrollUp) {
      const showOnScrollUp = scrollDistance + threshold > 0

      this.setHidden(this.isHidden && showOnScrollUp && isUnderHeader)
    } else if (isScrollDown) {
      const hideOnScrollDown = scrollDistance > threshold

      this.setHidden(hideOnScrollDown && isUnderHeader)
    }

    this.lastScrollPosition = scrollPosition
  }

  setHidden(isHidden: boolean): void {
    if (!this.sectionElement) {
      return
    }

    this.isHidden = isHidden

    this.sectionElement.setAttribute('aria-hidden', isHidden ? 'true' : 'false')
  }

  setSticky(): void {
    if (!this.sectionElement) {
      return
    }

    this.sectionElement.setAttribute('mobile-sticky', '')
    this.sectionElement.toggleAttribute('desktop-sticky', this.isSticky)
    document.body.toggleAttribute('header-desktop-sticky', this.isSticky)
  }

  updatePosition(): void {
    if (!this.sectionElement) {
      return
    }

    const headerRect = this.getBoundingClientRect()

    setStyleVariable('header-height', `${headerRect.height}px`)
    setStyleVariable('header-bottom-side', `${headerRect.bottom}px`)
  }

  get isSticky(): boolean {
    return this.hasAttribute('is-sticky')
  }

  get sectionElement(): Nullable<HTMLElement> {
    return document.querySelector('.shopify-section-header')
  }
}

export default HeaderComponent
